/**
 * configuracoes.js — Aplica as configurações da loja ao DOM.
 * Gerencia: cores, logo, banners (slider), contato e localização (maps).
 */

import { fetchConfiguracoes } from '../services/configuracoes.js';

// ── Estado interno do slider ────────────────────────────────────
let _currentSlide = 0;
let _totalSlides  = 0;
let _sliderTimer  = null;

/**
 * Ponto de entrada principal. Busca as configurações e aplica ao DOM.
 * Deve ser chamado no DOMContentLoaded de cada página.
 */
export async function applyConfiguracoes() {
  const { data, error } = await fetchConfiguracoes();

  if (error || !data) {
    console.warn('[configuracoes] Usando layout padrão — API indisponível.');
    applyBrandInfo({});
    applyHeroBanners({});
    applySobreInfo({});
    applyScrollObserver();
    document.body.classList.remove('config-loading');
    return;
  }

  applyColors(data);
  applyBrandInfo(data);
  applyContactInfo(data);
  applyHeroBanners(data);
  applySobreInfo(data);
  applyScrollObserver();
  document.body.classList.remove('config-loading');
}

// ── Cores ────────────────────────────────────────────────────────

function applyColors(config) {
  if (config.cor_primaria) {
    document.documentElement.style.setProperty('--primary-color', config.cor_primaria);
    
    // Usa texto primário configurado ou calcula contraste automático para texto sobre cor primária
    const contrast = config.texto_primario || (getContrastYIQ(config.cor_primaria) === 'white' ? '#ffffff' : '#171717');
    document.documentElement.style.setProperty('--primary-contrast', contrast);
  }
  
  // Aplica cor secundária (default: Orange if not present)
  const secColor = config.cor_secundaria || '#FF5A1F';
  document.documentElement.style.setProperty('--secondary-color', secColor);

  // Aplica contraste secundário configurado ou automático
  const secContrast = config.texto_secundario || (getContrastYIQ(secColor) === 'white' ? '#ffffff' : '#171717');
  document.documentElement.style.setProperty('--secondary-contrast', secContrast);
  
  // Atualiza tons de acento baseados na secundária para glows
  if (secColor.startsWith('#')) {
    const r = parseInt(secColor.slice(1, 3), 16);
    const g = parseInt(secColor.slice(3, 5), 16);
    const b = parseInt(secColor.slice(5, 7), 16);
    document.documentElement.style.setProperty('--accent-glow', `rgba(${r}, ${g}, ${b}, 0.4)`);
  }
}

/**
 * Calcula se a cor deve ter texto branco ou preto para melhor contraste.
 */
function getContrastYIQ(hexcolor) {
  if (!hexcolor || typeof hexcolor !== 'string') return 'white';
  const hex = hexcolor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return (yiq >= 128) ? 'black' : 'white';
}

// ── Marca / Logo ─────────────────────────────────────────────────

function applyBrandInfo(config) {
  const nome = config.nome_loja?.trim() || 'VoxCar';

  // Atualiza texto da marca em todos os elementos data-config
  if (nome) {
    document.querySelectorAll('[data-config="nome_loja"]').forEach(el => {
      el.textContent = nome;
    });
    document.querySelectorAll('[data-config="brand_initial"]').forEach(el => {
      el.textContent = nome[0].toUpperCase();
    });
  }

  // Descrição no footer
  const desc = config.descricao_curta?.trim() || 'Encontre o veículo dos seus sonhos com experiência de compra incomparável.';
  if (desc) {
    document.querySelectorAll('[data-config="descricao_curta"]').forEach(el => {
      el.textContent = desc;
    });
  }

  // Logo: toggle entre texto e imagem — sem innerHTML, sem flash
  if (config.logo_url) {
    const targets = ['', 'footer-'];
    targets.forEach(prefix => {
      const logoImg  = document.getElementById(`${prefix}brand-logo-img`);
      const logoLink = document.getElementById(`${prefix}brand-logo-link`);
      const brandIcon = document.getElementById(`${prefix}brand-icon`);
      const brandName = document.getElementById(`${prefix}brand-name`);

      if (logoImg) {
        logoImg.src = config.logo_url;
        logoImg.alt = nome || 'Logo';
      }
      if (logoLink) logoLink.style.display = '';
      if (brandIcon) brandIcon.style.display = 'none';
      if (brandName) brandName.style.display = 'none';
    });
  }
}

// ── Contato / Localização ────────────────────────────────────────

function applyContactInfo(config) {
  // Endereço
  const endEl = document.getElementById('config-endereco');
  if (endEl && config.endereco) endEl.textContent = config.endereco;

  // Telefone
  const telEl = document.getElementById('config-telefone');
  if (telEl && config.telefone) telEl.textContent = config.telefone;

  // E-mail
  const mailEl = document.getElementById('config-email');
  if (mailEl && config.email) {
    mailEl.textContent = config.email;
    mailEl.href = `mailto:${config.email}`;
  }

  // Google Maps iframe
  const mapsEl = document.getElementById('config-maps-iframe');
  if (mapsEl && config.link_maps) {
    mapsEl.src = config.link_maps;
  }

  // Mapa da página sobre.html
  const mapsSobreEl = document.getElementById('config-maps-iframe-sobre');
  if (mapsSobreEl && config.link_maps) {
    mapsSobreEl.src = config.link_maps;
  }

  // WhatsApp — links e botões com data-config="whatsapp_href"
  if (config.whatsapp) {
    const wppClean = config.whatsapp.replace(/\D/g, '');
    const baseUrl  = `https://wa.me/55${wppClean}`;

    document.querySelectorAll('[data-config="whatsapp_href"]').forEach(el => {
      el.href = baseUrl;
    });

    // Botão WhatsApp da sidebar de veiculo (é <button>, pega o click handler)
    const sidebarBtn = document.getElementById('btn-whatsapp-sidebar');
    if (sidebarBtn) {
      sidebarBtn.addEventListener('click', () => window.open(baseUrl, '_blank'));
    }

    // FAB WhatsApp
    const fabEl = document.getElementById('fab-whatsapp');
    if (fabEl) fabEl.href = baseUrl;

    // Atualiza botão da seção Vender
    import('./vender.js').then(({ atualizarWhatsappVender }) => {
      if (atualizarWhatsappVender) atualizarWhatsappVender(config.whatsapp);
    }).catch(err => console.warn('[vender] Não foi possível carregar módulo de venda', err));

    // Atualiza botão da seção Financiamento
    import('./financiamento.js').then(({ atualizarWhatsappFinanciamento }) => {
      if (atualizarWhatsappFinanciamento) atualizarWhatsappFinanciamento(config.whatsapp);
    }).catch(err => console.warn('[financiamento] Não foi possível carregar módulo', err));
  }

  // ── Redes sociais no footer (show/hide dinâmico) ─────────────────────────

  // WhatsApp — oculta o ícone se não configurado
  const footerWpp = document.getElementById('footer-whatsapp');
  if (footerWpp) {
    if (config.whatsapp) {
      const wppClean = config.whatsapp.replace(/\D/g, '');
      footerWpp.href = `https://wa.me/55${wppClean}`;
      footerWpp.style.display = '';
    } else {
      footerWpp.style.display = 'none';
    }
  }

  // Instagram
  const igEl = document.getElementById('footer-instagram');
  if (igEl) {
    if (config.instagram_url) {
      igEl.href = config.instagram_url;
      igEl.style.display = '';
    } else {
      igEl.style.display = 'none';
    }
  }

  // Facebook
  const fbEl = document.getElementById('footer-facebook');
  if (fbEl) {
    if (config.facebook_url) {
      fbEl.href = config.facebook_url;
      fbEl.style.display = '';
    } else {
      fbEl.style.display = 'none';
    }
  }

  // YouTube
  const ytEl = document.getElementById('footer-youtube');
  if (ytEl) {
    if (config.youtube_url) {
      ytEl.href = config.youtube_url;
      ytEl.style.display = '';
    } else {
      ytEl.style.display = 'none';
    }
  }

  // TikTok
  const ttEl = document.getElementById('footer-tiktok');
  if (ttEl) {
    if (config.tiktok_url) {
      ttEl.href = config.tiktok_url;
      ttEl.style.display = '';
    } else {
      ttEl.style.display = 'none';
    }
  }

  // Título da página e meta description com nome da loja
  if (config.nome_loja) {
    const titleEl = document.querySelector('title');
    if (titleEl && !titleEl.dataset.overridden) {
      titleEl.textContent = titleEl.textContent.replace(/VoxCar/gi, config.nome_loja);
    }
    const ogTitleEl = document.getElementById('og-title');
    if (ogTitleEl && !ogTitleEl.dataset.overridden) {
      ogTitleEl.content = ogTitleEl.content.replace(/VoxCar/gi, config.nome_loja);
    }
  }

  // Set OG Image from banner or logo as fallback
  const ogImageEl = document.getElementById('og-image');
  if (ogImageEl && !ogImageEl.dataset.overridden) {
    const imgUrl = config.banner_1_url || config.foto_showroom_url || config.logo_url;
    if (imgUrl) {
      ogImageEl.content = imgUrl;
    }
  }

  // Set Canonical
  const canonicalEl = document.getElementById('canonical-url');
  if (canonicalEl && !canonicalEl.dataset.overridden) {
    canonicalEl.href = window.location.origin + window.location.pathname;
  }

  // ── Favicon & PWA Manifest Dinâmico ─────────────────────────
  if (config.favicon_url || config.logo_url) {
    const applyDynamicIcons = (iconUrl) => {
      let favicon = document.querySelector('link[rel="icon"]');
      if (!favicon) {
        favicon = document.createElement('link');
        favicon.rel = 'icon';
        document.head.appendChild(favicon);
      }
      if (iconUrl.startsWith('data:')) {
        favicon.type = 'image/png'; // Garante que o navegador entenda o Base64
      }
      favicon.href = iconUrl;

      let appleIcon = document.querySelector('link[rel="apple-touch-icon"]');
      if (!appleIcon) {
        appleIcon = document.createElement('link');
        appleIcon.rel = 'apple-touch-icon';
        document.head.appendChild(appleIcon);
      }
      appleIcon.href = iconUrl;

      const manifest = {
        name: config.nome_loja || "VoxCar",
        short_name: config.nome_loja || "VoxCar",
        start_url: "/",
        display: "standalone",
        background_color: "#ffffff",
        theme_color: config.cor_primaria || "#000000",
        icons: [
          {
            src: iconUrl,
            sizes: "512x512",
            type: iconUrl.startsWith('data:') ? "image/png" : "image/x-icon",
            purpose: "any maskable"
          }
        ]
      };

      const manifestBlob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' });
      const manifestUrl = URL.createObjectURL(manifestBlob);

      let manifestLink = document.querySelector('link[rel="manifest"]');
      if (!manifestLink) {
        manifestLink = document.createElement('link');
        manifestLink.rel = 'manifest';
        document.head.appendChild(manifestLink);
      }
      manifestLink.href = manifestUrl;
    };

    if (config.favicon_url) {
      applyDynamicIcons(config.favicon_url);
    } else {
      // Para evitar que logos retangulares fiquem achatados como ícone de app,
      // desenhamos o logo centralizado num canvas quadrado invisível (512x512).
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const size = 512;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        
        const padding = 40;
        const drawSize = size - padding * 2;
        const scale = Math.min(drawSize / img.width, drawSize / img.height);
        const w = img.width * scale;
        const h = img.height * scale;
        const x = (size - w) / 2;
        const y = (size - h) / 2;
        
        ctx.clearRect(0, 0, size, size);
        ctx.drawImage(img, x, y, w, h);
        
        try {
          applyDynamicIcons(canvas.toDataURL('image/png'));
        } catch (e) {
          console.warn('[CORS] Erro ao extrair base64 do canvas. Usando logo_url original:', e);
          applyDynamicIcons(config.logo_url);
        }
      };
      img.onerror = () => {
        console.warn('[Favicon] Erro ao carregar imagem no canvas. Usando original.');
        applyDynamicIcons(config.logo_url);
      };
      
      // Evita cache do navegador que poderia vir sem cabeçalhos CORS
      img.src = config.logo_url + (config.logo_url.includes('?') ? '&' : '?') + 'cb=' + Date.now();
    }
  }

  // Theme Color Meta Tag
  if (config.cor_primaria) {
    let themeColor = document.querySelector('meta[name="theme-color"]');
    if (!themeColor) {
      themeColor = document.createElement('meta');
      themeColor.name = 'theme-color';
      document.head.appendChild(themeColor);
    }
    themeColor.content = config.cor_primaria;
  }
}

// ── Hero Banners ─────────────────────────────────────────────────

function applyHeroBanners(config) {
  const banners = [
    config.banner_1_url,
    config.banner_2_url,
    config.banner_3_url,
    config.banner_4_url,
    config.banner_5_url,
  ].filter(Boolean);

  // Fallback estático
  if (!banners.length) {
    banners.push('https://images.unsplash.com/photo-1614200187524-dc4b892acf16?q=80&w=2500&auto=format&fit=crop');
  }

  const slider = document.getElementById('hero-slider');
  if (!slider) return;

  const track       = slider.querySelector('.hero-track');
  const dotsWrapper = slider.querySelector('.hero-dots');
  if (!track) return;

  _currentSlide = 0;
  _totalSlides  = banners.length;

  // ── Monta slides ──────────────────────────────────────────────
  track.innerHTML = banners.map((url, i) => `
    <a href="/estoque" class="hero-slide" aria-hidden="${i !== 0}" data-index="${i}" style="display: block;">
      <img
        src="${url}"
        alt="Banner ${i + 1}"
        class="hero-slide-img"
        loading="${i === 0 ? 'eager' : 'lazy'}"
        fetchpriority="${i === 0 ? 'high' : 'auto'}"
      />
    </a>
  `).join('');

  // ── Monta dots ────────────────────────────────────────────────
  if (dotsWrapper) {
    if (banners.length > 1) {
      dotsWrapper.innerHTML = banners.map((_, i) => `
        <button
          class="hero-dot${i === 0 ? ' active' : ''}"
          aria-label="Banner ${i + 1}"
          data-index="${i}"
        ></button>
      `).join('');

      dotsWrapper.querySelectorAll('.hero-dot').forEach(dot => {
        dot.addEventListener('click', () => {
          goToSlide(parseInt(dot.dataset.index));
          resetAutoplay();
        });
      });
    } else {
      dotsWrapper.innerHTML = '';
    }
  }

  // ── Autoplay ──────────────────────────────────────────────────
  if (banners.length > 1) {
    startAutoplay();
  }
}

function goToSlide(index) {
  const slider = document.getElementById('hero-slider');
  if (!slider) return;

  const track = slider.querySelector('.hero-track');
  if (!track) return;

  _currentSlide = ((index % _totalSlides) + _totalSlides) % _totalSlides;

  track.style.transform = `translateX(-${_currentSlide * 100}%)`;

  // Atualiza aria-hidden dos slides
  slider.querySelectorAll('.hero-slide').forEach((slide, i) => {
    slide.setAttribute('aria-hidden', String(i !== _currentSlide));
  });

  // Atualiza dots
  slider.querySelectorAll('.hero-dot').forEach((dot, i) => {
    dot.classList.toggle('active', i === _currentSlide);
  });
}

function startAutoplay() {
  if (_sliderTimer) clearInterval(_sliderTimer);
  _sliderTimer = setInterval(() => goToSlide(_currentSlide + 1), 5000);
}

function resetAutoplay() {
  if (_sliderTimer) {
    clearInterval(_sliderTimer);
    startAutoplay();
  }
}
// ── Seção Sobre ──────────────────────────────────────────────────

function applySobreInfo(config) {
  // Descrição completa da loja
  const descEl = document.getElementById('config-sobre-descricao');
  if (descEl) {
    descEl.textContent = config.descricao || 'Oferecemos veículos inspecionados com laudo cautelar aprovado e garantia estendida. Nosso compromisso é com a sua total segurança e satisfação na escolha do seu próximo carro premium.';
  }

  // Foto do showroom (campo: foto_showroom_url)
  const imgEl = document.getElementById('config-sobre-img');
  if (imgEl) {
    imgEl.src = config.foto_showroom_url || 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?q=80&w=2073&auto=format&fit=crop';
    imgEl.alt = `Showroom ${config.nome_loja || 'VoxCar'}`.trim();
  }

  // Anos de experiência (campo: anos_experiencia — ex: "+15")
  const anosEl = document.getElementById('config-sobre-anos');
  if (anosEl) {
    anosEl.textContent = config.anos_experiencia || '+10';
  }
}

// ── Animations Observer ──────────────────────────────────────────

function applyScrollObserver() {
  const observerOptions = {
    threshold: 0.15,
    rootMargin: '0px 0px -50px 0px'
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('reveal-visible');
        // Uma vez visível, para de observar este elemento
        observer.unobserve(entry.target);
      }
    });
  }, observerOptions);

  document.querySelectorAll('[class*="reveal-"]').forEach(el => {
    observer.observe(el);
  });
}
